/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/
// React hooks and part code management service
import { useState, useEffect, useContext } from "react";
import partCodesService from "services/part-code-service";
import partCategoryService from "services/part-category-service";
import { useNavigate } from "react-router-dom";
import changeLogService from "services/change-log-service";
import { AuthContext } from "context";
import formatPartCode from "helper/formatPartCode";

// MUI and Material Dashboard Components
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import MDBox from "components/MDBox";
import FormField from "layouts/applications/wizard/components/FormField";
import MDInput from "components/MDInput";
import CircularProgress from '@mui/material/CircularProgress';

export default function AddPartCode({
    handleClose,
    open,
    children,
    setChildren,
    id
}) {
    const [parentCategories, setParentCategories] = useState([]); // Stores fetched Parent Categories
    const [selectedParentCategory, setSelectedParentCategory] = useState(""); // Stores the selected parentCategory
    const [partCategories, setPartCategories] = useState([]); // Stores fetched Part Categories
    const [selectedPartCategory, setSelectedPartCategory] = useState(""); // Stores the selected partCategory
    const [filteredPartCategories, setFilteredPartCategories] = useState([]); // Stores the part categories filtered by parent category
    const [partCodes, setPartCodes] = useState([]); // Stores fetched Part Codes
    const [selectedPartCode, setSelectedPartCode] = useState(""); // Stores the selected partCode
    const [fileteredPartCodes, setFilteredPartCodes] = useState([]); // Stores part codes filtered by part category
    const [isLoading, setIsLoading] = useState(false);

    const [showButton, setShowButton] = useState(false);

    const authContext = useContext(AuthContext);

    // Used to store the additional notes and quantity of the part code in the BOM
    const [additionalData, SetAdditionalData] = useState({
        text: "",
        quantity: 1
    });


    // Fetches the parent categories, part categories and part codes
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const response1 = await partCategoryService.getAllParentCategories();
                const response2 = await partCategoryService.getAllPartCategories();
                const reponse3 = await partCodesService.getParts();
                setPartCategories(response2.data.attributes);
                setParentCategories(response1.data.attributes);

                const allowedStatuses = new Set(["approved", "pending approval", "rejected"]);
                const approvedParts = (reponse3?.data?.attributes ?? []).filter(
                    (part) => allowedStatuses.has(String(part?.status ?? "").toLowerCase())
                );
                
                setPartCodes(approvedParts);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    // Handles Category Change
    useEffect(() => {
        if (selectedParentCategory) {
            const filterpartcategories = partCategories.filter((partCategory) => partCategory.parentCategory === selectedParentCategory);
            setFilteredPartCategories(filterpartcategories);
        } else {
            // Empties the fields related to parentCategory if no parentCategory is selected
            setFilteredPartCategories([]);
        }
        setSelectedPartCategory("");
        //changeHandler({ target: { name: "partCategory.parentCategory", value: selectedParentCategory } });
    }, [selectedParentCategory]);

    // Handles Part Code change
    useEffect(() => {
        if (selectedPartCategory) {
            const filterparts = partCodes.filter((part) => part.partCategory.categoryCode === selectedPartCategory.categoryCode);
            const formattedPartCodes = filterparts.map((part) => ({
                ...part,
                partCode: formatPartCode(part)
            }));
            setFilteredPartCodes(formattedPartCodes);
        } else {
            // Empties the fields related to partCategory if no partCategory is selected
            setFilteredPartCodes([]);
        }
        setSelectedPartCode("");
        //changeHandler({ target: { name: "partCategory.categoryCode", value: selectedPartCategory } });
    }, [selectedPartCategory, partCodes]);

    useEffect(() => {
        if (selectedPartCode !== "") {
            setShowButton(true);
        } else {
            setShowButton(false);
        }
    }, [selectedPartCode]);

    const commonProps = { // Props common for the input fields
        display: "flex",
        flexDirection: "column",
        fullWidth: true,
        marginBottom: "1rem",
        marginTop: "1rem"
    };

    // Handles the addition of a part code
    const handleAdd = async () => {
        setChildren([...children, selectedPartCode._id]);
        // Format the payload to include the assembly's previous children and the new child (partcode)
        const payload = {
            data: {
                type: "PartCode",
                id: id,
                attributes: {
                    children: [
                        ...children,
                        {
                            part: selectedPartCode._id,
                            text: additionalData.text,
                            Quantity: additionalData.quantity
                        }
                    ],
                }
            }

        };
        await partCodesService.updateChildren(payload, id);
        const response = await partCodesService.getPartById(id);
        const uid = formatPartCode(response.data.attributes);

        // Change Log Entry
        const changeLog = {
            data: {
                attributes: {
                    partCode: selectedPartCode._id,
                    change: {
                        type: "Added to BOM",
                        description: `Added to ${uid}`,
                        date: new Date(),
                        user: await authContext.getCurrentUser()
                    }
                }
            }
        }

        await changeLogService.addChange(changeLog);
        handleClose();
    };

    // Change Handler for the additional notes and quantity fields
    const changeHandler = (e) => {
        SetAdditionalData({
            ...additionalData,
            [e.target.name]: e.target.value
        })
    }


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Select a Part Code to add</DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <MDBox margin={2}>
                        {/* Parent Category */}

                        <MDBox {...commonProps}>
                            <Autocomplete
                                defaultValue=""
                                options={parentCategories}
                                getOptionLabel={(option) => option}
                                value={selectedParentCategory ?? ""}
                                onChange={(event, newValue) => {
                                    setSelectedParentCategory(newValue);
                                }}
                                renderInput={(params) => (
                                    <FormField {...params} label="Parent Category" InputLabelProps={{ shrink: true }} />
                                )} />

                        </MDBox>


                        {/* Part Category*/}

                        <MDBox {...commonProps}>
                            <Autocomplete
                                defaultValue=""
                                options={filteredPartCategories}
                                getOptionLabel={(option) => (option ? option.categoryCode : "")}
                                value={selectedPartCategory ?? ""}
                                onChange={(event, newValue) => {
                                    setSelectedPartCategory(newValue);
                                }}
                                renderInput={(params) => (
                                    <FormField {...params} label="Part Category" InputLabelProps={{ shrink: true }} />
                                )} />
                        </MDBox>

                        {/* Part Code */}
                        <MDBox {...commonProps}>
                            <Autocomplete
                                defaultValue=""
                                options={fileteredPartCodes}
                                getOptionLabel={(option) => (option ? option.partCode : "")}
                                value={selectedPartCode ?? ""}
                                onChange={(event, newValue) => {
                                    setSelectedPartCode(newValue);
                                }}
                                renderInput={(params) => (
                                    <FormField {...params} label="Part Code" InputLabelProps={{ shrink: true }} />
                                )} />
                        </MDBox>

                        {/* Additional Notes Field */}
                        <MDBox {...commonProps}>
                            <MDInput
                                label="Additional Notes"
                                name="text"
                                value={additionalData.text}
                                onChange={changeHandler}
                                variant="standard"
                                fullWidth
                            />
                        </MDBox>

                        {/* Quantity Field */}
                        <MDBox {...commonProps}>
                            <MDInput
                                label="Quantity"
                                name="quantity"
                                type="number"
                                value={additionalData.quantity}
                                onChange={changeHandler}
                                variant="standard"
                                fullWidth
                            />
                        </MDBox>

                    </MDBox>
                )}
            </DialogContent>
            <DialogActions>
                {/* Button to view the selected part code's specific view*/}
                {selectedPartCode !== "" && (
                    <Button>
                        <a
                            href={selectedPartCode ? `/part-code-management/view-part-code/${selectedPartCode._id}` : "#"}
                            style={{ textDecoration: "none" }}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View Selected Part Code
                        </a>
                    </Button>
                )}

                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleAdd}>Add</Button>

            </DialogActions>
        </Dialog>
    )
}